import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children"];
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { createSelector } from 'reselect';
import { NullContainer } from '@beef/utils';
import { injectServices, selectDefaultId } from "./helpers";
import { __SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED } from "./internals";
import { composeCurrentKeySelector } from "./storage";
import { CurrentBulkProvider } from "./Provider";
var generateCurrentKey = __SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.generateCurrentKey,
  createMapperCurrentSelectors = __SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.createMapperCurrentSelectors,
  createProvider = __SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.createProvider,
  createMapperHOC = __SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.createMapperHOC;

// TODO: WORK IN PROGRESS
//   планируется дополнительный метод injectCurrent - обертка для селекторов,
//   которая позволит переопределять произвольные селекторы

var selectRootState = function selectRootState(state) {
  return state;
};

/**
 * Define selector as injectable
 * @param KEY {string|Symbol} use null for automatically generated key
 * @returns {OutputSelector<unknown, *, (res1: unknown, res2: unknown) => *>}
 */
export var createInjectableSelector = function createInjectableSelector() {
  var KEY = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : generateCurrentKey();
  var currentSelector = createSelector([selectRootState, composeCurrentKeySelector(KEY)], function (state, injectedSelector) {
    return injectedSelector(state);
  });
  Object.assign(currentSelector, {
    KEY: KEY
  });
  return currentSelector;
};
export var createInjectableCallback = function createInjectableCallback() {
  var KEY = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : generateCurrentKey();
  var currentHandler = function currentHandler() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return function (dispatch, getState) {
      var state = getState();
      var handler = composeCurrentKeySelector(KEY)(state);
      return handler.apply(void 0, args);
    };
  };
  Object.assign(currentHandler, {
    KEY: KEY
  });
  return currentHandler;
};

/**
 * @deprecated use createInjectableSelector instead
 */
export var createInjectableCurrent = createInjectableSelector;

/**
 * Syntax sugar for redux-current most common use case - creating mappers from list selector
 * @function
 * @template I
 * @param selectDataList {(state: Record) => I[]} a selector, that returns array of items to map
 * @param selectId {(item: I) => any}}a local selector that given an (item, index), returns the element
 * @param KEY {string|Symbol=} omit for automatically generated key
 */
export var createCurrentSelector = function createCurrentSelector(selectDataList) {
  var selectId = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : selectDefaultId;
  var KEY = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : generateCurrentKey();
  /** short-circuit for currentSelector with no base selector */
  if (!selectDataList) return createInjectableSelector(KEY);
  var _createMapperCurrentS = createMapperCurrentSelectors(selectDataList, selectId, KEY),
    selectCurrent = _createMapperCurrentS.selectCurrent,
    selectIds = _createMapperCurrentS.selectIds;
  var CurrentProvider = createProvider(KEY);
  var withCurrentProviderMapper = createMapperHOC(CurrentProvider, selectIds);
  var CurrentProviderMapper = withCurrentProviderMapper(NullContainer);

  /** Let all these props be available as properties of the main function */
  Object.assign(selectCurrent, {
    withCurrentProviderMapper: withCurrentProviderMapper,
    CurrentProvider: CurrentProvider,
    CurrentProviderMapper: CurrentProviderMapper,
    selectCurrent: selectCurrent,
    selectIds: selectIds,
    KEY: KEY
  });

  /** In all the possible cases, only the selector will always be used */
  return selectCurrent;
};

/**
 * Shorthand for fast destructuring of the most useful case: [selector, HOC]
 * @param selectDataList
 * @param selectId
 * @param KEY {string|Symbol=}
 * @returns {[undefined,undefined]}
 */
export var createCurrentHOC = function createCurrentHOC(selectDataList, selectId, KEY) {
  var _createCurrentSelecto = createCurrentSelector(selectDataList, selectId, KEY),
    selectCurrent = _createCurrentSelecto.selectCurrent,
    withCurrentProviderMapper = _createCurrentSelecto.withCurrentProviderMapper;
  return [selectCurrent, withCurrentProviderMapper];
};

/**
 * @deprecated use createCurrentSelector instead
 */
export var createCurrentAdapter = createCurrentSelector;

/**
 *
 * @param injectables {Record<{selector: string}, {selector: function}>}
 *   A dictionary of selectors that must be injected
 */
export var createCurrentModule = function createCurrentModule(injectables) {
  var CurrentModule = function CurrentModule(_ref) {
    var children = _ref.children,
      concreteValues = _objectWithoutProperties(_ref, _excluded);
    var values = useMemo(function () {
      return injectServices(injectables, concreteValues);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Object.values(concreteValues));
    return /*#__PURE__*/React.createElement(CurrentBulkProvider, {
      values: values
    }, typeof children === 'function' ? children(values) : children);
  };
  CurrentModule.propTypes = process.env.NODE_ENV !== "production" ? {
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
  } : {};
  return CurrentModule;
};